<template>
  <b-overlay :show="isLoading" rounded="sm">
    <finance-week-header :week="week" />

    <b-card v-if="week" title="Игры недели">
      <vue-good-table
        :rows="week.games"
        :columns="gamesColumns"
        :pagination-options="{ enabled: false }"
        :sort-options="{ enabled: false }"
        :row-style-class="gameRowClass"
      >
        <template slot="emptystate">
          <div class="text-center text-muted py-2">Нет игр</div>
        </template>
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'index'">{{ props.index + 1 }}</span>
          <span v-else-if="props.column.field === 'date'">{{ formatDate(props.row.date) }}</span>
          <span v-else-if="props.column.field === 'name'">
            <b-link
              v-if="props.row.id"
              :href="planfixTaskUrl(props.row.id)"
              target="_blank"
              rel="noopener"
            >
              {{ props.row.name }}
            </b-link>
            <span v-else>{{ props.row.name }}</span>
            <b-badge v-if="props.row.is_private" variant="light-info" class="ml-50">
              Частная
            </b-badge>
          </span>
          <span v-else-if="props.column.field === 'revenue'">{{ formatRub(props.row.revenue) }}</span>
          <span v-else-if="props.column.field === 'host_expense'">{{ formatRub(props.row.host_expense) }}</span>
          <span v-else-if="props.column.field === 'host_paid_sum'">
            <b-badge
              v-if="isHostPaid(props.row)"
              variant="light-success"
            >
              {{ formatRub(props.row.host_paid_sum) }}
            </b-badge>
            <span v-else>{{ formatRub(props.row.host_paid_sum) }}</span>
          </span>
          <span v-else>{{ props.formattedRow[props.column.field] }}</span>
        </template>
      </vue-good-table>
    </b-card>

    <b-card v-if="week" title="Пакеты недели">
      <vue-good-table :rows="week.packs" :columns="packsColumns" :pagination-options="{ enabled: false }">
        <template slot="emptystate">
          <div class="text-center text-muted py-2">Нет пакетов</div>
        </template>
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'pack'">
            <div>{{ props.row.pack_name || '—' }}</div>
            <small>
              <b-link
                v-if="props.row.pack_id"
                :href="planfixPackUrl(props.row.pack_id)"
                target="_blank"
                rel="noopener"
              >
                #{{ props.row.pack_id }}
              </b-link>
              <span v-else class="text-muted">—</span>
            </small>
          </span>
          <span v-else-if="props.column.field === 'author'">
            {{ (props.row.author && props.row.author.name) || (props.row.author && props.row.author.id) || '—' }}
          </span>
          <span v-else-if="props.column.field === 'gamesPlayed'">
            {{ props.row.games_count }}
          </span>
          <span v-else-if="props.column.field === 'private'">
            <template v-if="props.row.private && props.row.private.games_count">
              <div>{{ props.row.private.games_count }}/{{ props.row.games_count }}</div>
              <small class="text-muted">{{ formatRub(props.row.private.revenue) }}</small>
            </template>
            <span v-else class="text-muted">—</span>
          </span>
          <span v-else-if="props.column.field === 'revenue'">{{ formatRub(props.row.revenue) }}</span>
          <span v-else-if="props.column.field === 'expenses'">
            <div><small>Х: {{ formatRub(props.row.expenses && props.row.expenses.hosts) }}</small></div>
            <div><small>А: {{ formatRub(props.row.expenses && props.row.expenses.authors) }}</small></div>
            <div><strong>≡ {{ formatRub(props.row.expenses && props.row.expenses.total) }}</strong></div>
          </span>
          <span v-else-if="props.column.field === 'profit'" :class="`text-${profitVariant(props.row.profit)}`">
            {{ formatRub(props.row.profit) }}
          </span>
          <span v-else-if="props.column.field === 'margin'">{{ formatPercent(props.row.margin) }}</span>
          <span v-else-if="props.column.field === 'task'">
            <b-link
              v-if="props.row.planfix_task_id"
              :href="planfixTaskUrl(props.row.planfix_task_id)"
              target="_blank"
              rel="noopener"
            >
              {{ props.row.task_name || `#${props.row.planfix_task_id}` }}
            </b-link>
            <span v-else class="text-muted">—</span>
          </span>
          <span v-else-if="props.column.field === 'status'">
            <b-badge v-if="props.row.status" :variant="`light-${packStatusVariant(props.row.status)}`">
              {{ props.row.status }}
            </b-badge>
            <span v-else class="text-muted">—</span>
          </span>
          <span v-else>{{ props.formattedRow[props.column.field] }}</span>
        </template>
      </vue-good-table>
    </b-card>

    <b-alert v-if="allPaid" show variant="success" class="mb-0">
      <feather-icon icon="CheckCircleIcon" class="mr-50" />
      Всё оплачено по этой неделе
    </b-alert>

    <b-row v-else-if="week">
      <b-col cols="12" md="6" class="mb-1">
        <b-card title="Ведущим">
          <h5 class="mb-1">Итого: {{ formatRub(unpaidHostsTotal) }}</h5>
          <vue-good-table v-if="unpaid.hosts.length" :rows="unpaid.hosts" :columns="unpaidHostsColumns" :pagination-options="{ enabled: false }">
            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field === 'amount'">{{ formatRub(props.row.amount) }}</span>
              <span v-else-if="props.column.field === 'recipient'">{{ props.row.recipient.name }}</span>
              <span v-else-if="props.column.field === 'task'">
                <b-link
                  v-if="props.row.planfix_task_id"
                  :href="planfixTaskUrl(props.row.planfix_task_id)"
                  target="_blank"
                  rel="noopener"
                >
                  #{{ props.row.planfix_task_id }}
                </b-link>
                <span v-else class="text-muted">—</span>
              </span>
              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
          </vue-good-table>
          <div v-else class="text-muted">Нет неоплаченных</div>
        </b-card>
      </b-col>
      <b-col cols="12" md="6" class="mb-1">
        <b-card title="Авторам">
          <h5 class="mb-1">Итого: {{ formatRub(unpaidAuthorsTotal) }}</h5>
          <vue-good-table v-if="unpaid.authors.length" :rows="unpaid.authors" :columns="unpaidAuthorsColumns" :pagination-options="{ enabled: false }">
            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field === 'amount'">{{ formatRub(props.row.amount) }}</span>
              <span v-else-if="props.column.field === 'recipient'">{{ props.row.recipient.name }}</span>
              <span v-else-if="props.column.field === 'task'">
                <b-link
                  v-if="props.row.planfix_task_id"
                  :href="planfixTaskUrl(props.row.planfix_task_id)"
                  target="_blank"
                  rel="noopener"
                >
                  #{{ props.row.planfix_task_id }}
                </b-link>
                <span v-else class="text-muted">—</span>
              </span>
              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
          </vue-good-table>
          <div v-else class="text-muted">Нет неоплаченных</div>
        </b-card>
      </b-col>
    </b-row>
  </b-overlay>
</template>

<script>
import {
  BCard, BRow, BCol, BBadge, BLink, BOverlay, BAlert,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import { ref, computed, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import financeStoreModule from '@/views/finance/financeStoreModule'
import FinanceWeekHeader from '@/views/finance/components/FinanceWeekHeader.vue'
import {
  formatRub, formatPercent, profitVariant, planfixTaskUrl, planfixPackUrl,
} from '@/views/finance/utils'
import { formatDate } from '@core/utils/filter'

const STORE_MODULE_NAME = 'app-finance'

export default {
  components: {
    BCard, BRow, BCol, BBadge, BLink, BOverlay, BAlert,
    VueGoodTable, FinanceWeekHeader,
  },
  setup(_, { root }) {
    const toast = useToast()

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, financeStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const isLoading = ref(false)
    const week = ref(null)

    const gamesColumns = [
      { label: '#', field: 'index', sortable: false, width: '50px' },
      { label: 'Дата', field: 'date' },
      { label: 'Игра', field: 'name' },
      { label: 'Игроков', field: 'players_count', type: 'number' },
      { label: 'Выручка', field: 'revenue' },
      { label: 'Начислено ведущему', field: 'host_expense' },
      { label: 'Оплачено ведущему', field: 'host_paid_sum' },
    ]

    const isHostPaid = row => {
      if (!row || !row.host_paid_sum) return false
      const expense = row.host_expense || 0
      if (expense === 0) return row.host_paid_sum > 0
      return row.host_paid_sum >= expense
    }

    const gameRowClass = row => {
      if (!row || !row.date) return ''
      const d = new Date(String(row.date).replace(' ', 'T'))
      if (Number.isNaN(d.getTime())) return ''
      const day = d.getDay()
      return day === 0 || day === 5 || day === 6 ? 'finance-weekend-row' : ''
    }

    const packsColumns = [
      { label: 'Пакет', field: 'pack' },
      { label: 'Автор', field: 'author' },
      { label: 'Игр', field: 'gamesPlayed', sortable: false },
      { label: 'Частные', field: 'private', sortable: false },
      { label: 'Игроков', field: 'players_count', type: 'number' },
      { label: 'Выручка', field: 'revenue', type: 'number' },
      { label: 'Расходы', field: 'expenses', sortable: false },
      { label: 'Прибыль', field: 'profit', type: 'number' },
      { label: 'Маржа', field: 'margin', type: 'number' },
      { label: 'Задача', field: 'task' },
      { label: 'Статус', field: 'status' },
    ]

    const packStatusVariant = status => {
      if (status === 'Оплачено' || status === 'Завершенная') return 'success'
      if (status === 'Выполненная') return 'warning'
      if (status === 'В работе') return 'primary'
      if (status === 'Новая') return 'info'
      return 'secondary'
    }

    const unpaidHostsColumns = [
      { label: 'Дата', field: 'due_date' },
      { label: 'Название', field: 'title' },
      { label: 'Получатель', field: 'recipient' },
      { label: 'Сумма', field: 'amount' },
      { label: 'Задача', field: 'task' },
    ]

    const unpaidAuthorsColumns = [
      { label: 'Автор', field: 'recipient' },
      { label: 'Сумма', field: 'amount' },
      { label: 'Задача', field: 'task' },
    ]

    const unpaid = computed(() => {
      if (!week.value || !week.value.unpaid) return { hosts: [], authors: [] }
      return {
        hosts: week.value.unpaid.hosts || [],
        authors: week.value.unpaid.authors || [],
      }
    })

    const allPaid = computed(() => {
      if (!week.value) return false
      return unpaid.value.hosts.length === 0 && unpaid.value.authors.length === 0
    })

    const sumAmount = list => list.reduce((acc, item) => acc + (item.amount || 0), 0)
    const unpaidHostsTotal = computed(() => sumAmount(unpaid.value.hosts))
    const unpaidAuthorsTotal = computed(() => sumAmount(unpaid.value.authors))

    const fetchWeek = () => {
      isLoading.value = true
      store.dispatch('app-finance/fetchWeek', root.$route.params.id)
        .then(response => {
          week.value = response.data.data
        })
        .catch(err => {
          if (err.response?.status === 404) {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Неделя не найдена',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
            root.$router.push({ name: 'finance', query: { tab: 'weeks' } })
            return
          }
          toast({
            component: ToastificationContent,
            props: {
              title: err.response?.data?.message || 'Ошибка загрузки финансового отчёта',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    fetchWeek()

    return {
      isLoading,
      week,
      unpaid,
      gamesColumns,
      packsColumns,
      unpaidHostsColumns,
      unpaidAuthorsColumns,
      allPaid,
      unpaidHostsTotal,
      unpaidAuthorsTotal,
      isHostPaid,
      gameRowClass,
      packStatusVariant,
      formatRub,
      formatPercent,
      profitVariant,
      formatDate,
      planfixTaskUrl,
      planfixPackUrl,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';

.finance-weekend-row > td {
  background-color: rgba(40, 199, 111, 0.08);
}
</style>
